import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Watch, ArrowRight, Mail, ShieldCheck } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { Gauge, BarChart } from "../components/MiniChart";
import { loadDiary, diaryStats, predictFlare, anyWearableConnected, WEARABLE_DATA, riskColor, DAYS } from "../phase2";

const impactStyle = { high: "bg-[#fdeceb] text-[#e0567a]", med: "bg-[#fdf2e8] text-[#e08a2b]", low: "bg-slate-100 text-slate-500" };

export default function FlarePrediction() {
  const navigate = useNavigate();
  const connected = anyWearableConnected();
  const stats = useMemo(() => diaryStats(loadDiary()), []);
  const pred = useMemo(() => predictFlare(stats, WEARABLE_DATA, connected), [stats, connected]);
  const color = riskColor(pred.level);

  return (
    <AppLayout title="Symptom Forecast">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Gauge */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-col items-center justify-center">
          <div className="relative" style={{ width: 160, height: 160 }}>
            <Gauge value={pred.risk} color={color} size={160} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-head font-bold" style={{ color }}>{pred.risk}%</span>
              <span className="text-[13px] font-semibold" style={{ color }}>{pred.level} risk</span>
            </div>
          </div>
          <p className="text-[13px] text-slate-500 mt-3 text-center">Estimated symptom-episode risk over the next 7 days</p>
        </div>

        {/* Factors */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-head font-semibold text-slate-800 flex items-center gap-2"><Activity size={18} className="text-[#0d9488]" /> Contributing factors</h3>
          {!connected && (
            <button onClick={() => navigate("/asa/wearables")} className="mt-3 w-full flex items-center justify-between bg-[#e6f4f2] rounded-lg px-4 py-3 text-left hover:bg-[#d8ede9] transition-colors">
              <span className="text-[13px] text-[#0f766e] font-medium flex items-center gap-2"><Watch size={16} /> Connect a wearable to improve prediction accuracy</span>
              <ArrowRight size={16} className="text-[#0f766e]" />
            </button>
          )}
          <div className="mt-4 space-y-2">
            {pred.factors.length ? pred.factors.map((f, i) => (
              <div key={i} className="flex items-center justify-between border border-slate-100 rounded-lg px-4 py-3">
                <span className="text-[14px] text-slate-700">{f.label}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase ${impactStyle[f.impact]}`}>{f.impact} impact</span>
              </div>
            )) : <p className="text-sm text-slate-400">Not enough data yet — log symptoms and connect a wearable.</p>}
          </div>
        </div>
      </div>

      {/* 7-day forecast */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-5">
        <h3 className="font-head font-semibold text-slate-800 mb-4">7-day risk forecast</h3>
        <BarChart data={pred.forecast.map((f) => f.risk)} labels={pred.forecast.map((f) => f.day)} color={color} height={120} />
      </div>

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 mt-5">
        <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-[13px] text-amber-700">This symptom forecast is an estimate based on your data and is <strong>not a medical diagnosis</strong>. Seek medical advice if you feel unwell.</p>
      </div>
      <p className="text-[12px] text-slate-400 flex items-center gap-1.5 mt-3"><ShieldCheck size={13} /> Prediction summaries are stored securely in your ASAL account.</p>
    </AppLayout>
  );
}
